import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Card,
  Empty,
  Form,
  Input,
  List,
  Modal,
  Space,
  Spin,
  Typography,
  message,
} from "antd";
import {
  CloudSyncOutlined,
  DeleteOutlined,
  EditOutlined,
  MenuOutlined,
  PlusOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../firebase/firebase";
import { useAuth } from "../../context/AuthContext";
import { LinkRecommendService } from "./LinkRecommendService";
import {
  LinkRecommendFormValues,
  LinkRecommendItem,
  LinkRecommendKind,
} from "./web_parts/types";

const { Text } = Typography;
const { TextArea } = Input;

interface Setting_LinkRecommendProps {
  kind: LinkRecommendKind;
}

const Setting_LinkRecommend: React.FC<Setting_LinkRecommendProps> = ({ kind }) => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [items, setItems] = useState<LinkRecommendItem[]>([]);
  const [version, setVersion] = useState(0);
  const [dirty, setDirty] = useState(false);
  const [formVisible, setFormVisible] = useState(false);
  const [editing, setEditing] = useState<LinkRecommendItem | null>(null);
  const [form] = Form.useForm<LinkRecommendFormValues>();
  const [dragIndex, setDragIndex] = useState<number | null>(null);

  const isWeb = kind === "web";
  const label = isWeb ? "Webおすすめ" : "Youtubeおすすめ";
  const urlLabel = isWeb ? "URL" : "検索キーワード";

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const [list, ver] = await Promise.all([
        LinkRecommendService.getItems(kind),
        LinkRecommendService.getMetaVersion(),
      ]);
      setItems(list);
      setVersion(ver);
      setDirty(false);
    } catch (e) {
      console.error(e);
      message.error(`${label}の読み込みに失敗しました`);
    } finally {
      setLoading(false);
    }
  }, [kind, label]);

  useEffect(() => {
    void loadItems();
  }, [loadItems]);

  const notifyDevices = useCallback(async (): Promise<number> => {
    const snapshot = await getDocs(collection(db, "devices"));
    const tokens = snapshot.docs
      .map((d) => d.data())
      .filter((data) => data.isDeleted !== true)
      .map((data) => (typeof data.fcmToken === "string" ? data.fcmToken.trim() : ""))
      .filter((token) => token.length > 0);

    if (tokens.length === 0) return 0;

    await axios.post(
      "/api/sendFCM",
      {
        tokens,
        payload: {
          mode: "request",
          command: "update_link_recommend",
          user: currentUser?.displayName || currentUser?.email || "admin",
          place: "WEB",
          param1: String(Date.now()),
          param2: kind,
        },
      },
      { headers: { "Content-Type": "application/json" } }
    );
    return tokens.length;
  }, [currentUser, kind]);

  const handleNotifyDevices = async () => {
    setSyncing(true);
    try {
      const count = await notifyDevices();
      if (count === 0) {
        message.warning("通知可能な端末（FCMトークン付き）が見つかりませんでした");
      } else {
        message.success(`${count} 台の端末へ同期通知を送信しました`);
      }
    } catch (e) {
      console.error(e);
      message.error("端末への同期通知に失敗しました");
    } finally {
      setSyncing(false);
    }
  };

  const openForm = (item?: LinkRecommendItem) => {
    form.resetFields();
    if (item) {
      setEditing(item);
      form.setFieldsValue({
        title: item.title,
        description: item.description,
        url: item.url,
      });
    } else {
      setEditing(null);
    }
    setFormVisible(true);
  };

  const handleFormSave = (values: LinkRecommendFormValues) => {
    if (editing) {
      setItems((prev) =>
        prev.map((row) =>
          row.localId === editing.localId
            ? {
                ...row,
                title: values.title.trim(),
                description: values.description.trim(),
                url: values.url.trim(),
              }
            : row
        )
      );
    } else {
      setItems((prev) => [
        ...prev,
        {
          localId: `new_${Date.now()}`,
          title: values.title.trim(),
          description: values.description.trim(),
          url: values.url.trim(),
          sortOrder: prev.length,
        },
      ]);
    }
    setDirty(true);
    setFormVisible(false);
  };

  const handleDelete = (item: LinkRecommendItem) => {
    Modal.confirm({
      title: "削除の確認",
      content: `「${item.title}」をリストから削除しますか？`,
      okText: "削除",
      okType: "danger",
      cancelText: "キャンセル",
      onOk: () => {
        setItems((prev) => prev.filter((row) => row.localId !== item.localId));
        setDirty(true);
      },
    });
  };

  const handleLoadDefaults = () => {
    Modal.confirm({
      title: "初期データを読み込む",
      content: "現在の編集内容を破棄し、アプリ同梱の初期おすすめ一覧を読み込みます。保存するまで端末には反映されません。",
      okText: "読み込む",
      cancelText: "キャンセル",
      onOk: () => {
        setItems(LinkRecommendService.defaultItems(kind));
        setDirty(true);
      },
    });
  };

  const handleDragStart = (index: number) => setDragIndex(index);
  const handleDrop = (index: number) => {
    if (dragIndex === null || dragIndex === index) {
      setDragIndex(null);
      return;
    }
    setItems((prev) => {
      const next = [...prev];
      const [moved] = next.splice(dragIndex, 1);
      next.splice(index, 0, moved);
      return next.map((row, i) => ({ ...row, sortOrder: i }));
    });
    setDirty(true);
    setDragIndex(null);
  };

  const handleSaveAndNotify = async () => {
    setSaving(true);
    try {
      const nextVersion = await LinkRecommendService.saveItems(kind, items);
      setVersion(nextVersion);
      setDirty(false);
      message.success(`${label}を保存しました（version ${nextVersion}）`);
      try {
        const count = await notifyDevices();
        if (count > 0) {
          message.info(`${count} 台の端末へ同期通知を送信しました`);
        } else {
          message.warning("保存は完了しましたが、通知可能な端末がありませんでした");
        }
      } catch (fcmError) {
        console.error(fcmError);
        message.warning("保存は完了しましたが、端末への同期通知に失敗しました");
      }
    } catch (e) {
      console.error(e);
      message.error(`${label}の保存に失敗しました`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <Space style={{ marginBottom: 16 }} wrap>
        <Button icon={<ReloadOutlined />} onClick={() => void loadItems()} disabled={loading || saving}>
          再読み込み
        </Button>
        <Button onClick={handleLoadDefaults} disabled={loading || saving}>
          初期データを読み込む
        </Button>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => openForm()}
          disabled={loading || saving}
        >
          追加
        </Button>
        <Button
          type="primary"
          loading={saving}
          disabled={loading || !dirty}
          onClick={() => void handleSaveAndNotify()}
        >
          保存して全端末へ通知
        </Button>
        <Button
          icon={<CloudSyncOutlined />}
          loading={syncing}
          disabled={loading || dirty}
          onClick={() => void handleNotifyDevices()}
        >
          同期通知のみ再送
        </Button>
      </Space>

      <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
        全端末共通の「旬のおすすめ」です。保存時に version を更新し、FCM（update_link_recommend）で端末へ通知します。
        端末は通常 meta の版数だけを確認し、変化時のみ一覧を取得します（24時間ごとの保険ポーリングあり）。
        {dirty ? " ※未保存の変更があります。" : ` 現在の version: ${version}`}
      </Text>

      <Card title={`${label}一覧`}>
        <Spin spinning={loading}>
          {items.length === 0 ? (
            <Empty description="未登録です。「初期データを読み込む」または「追加」から作成してください。" />
          ) : (
            <List
              itemLayout="horizontal"
              dataSource={items}
              renderItem={(item, index) => (
                <List.Item
                  key={item.localId}
                  draggable
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => handleDrop(index)}
                  style={{
                    cursor: "move",
                    background: dragIndex === index ? "#f0f8ff" : "transparent",
                  }}
                  actions={[
                    <Button key="edit" icon={<EditOutlined />} onClick={() => openForm(item)} />,
                    <Button
                      key="del"
                      icon={<DeleteOutlined />}
                      danger
                      onClick={() => handleDelete(item)}
                    />,
                  ]}
                >
                  <List.Item.Meta
                    avatar={<MenuOutlined style={{ cursor: "grab", color: "#999" }} />}
                    title={item.title}
                    description={`${urlLabel}: ${item.url}`}
                  />
                </List.Item>
              )}
            />
          )}
        </Spin>
      </Card>

      <Modal
        title={editing ? "リンクの編集" : "リンクの追加"}
        open={formVisible}
        onCancel={() => setFormVisible(false)}
        onOk={() => form.submit()}
        okText="反映"
        cancelText="キャンセル"
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleFormSave}>
          <Form.Item name="title" label="タイトル" rules={[{ required: true, message: "必須項目です" }]}>
            <Input />
          </Form.Item>
          <Form.Item
            name="url"
            label={urlLabel}
            rules={[
              { required: true, message: "必須項目です" },
              ...(isWeb ? [{ type: "url" as const, message: "有効なURLを入力してください" }] : []),
            ]}
          >
            <Input placeholder={isWeb ? "https://..." : "検索キーワード"} />
          </Form.Item>
          <Form.Item
            name="description"
            label="説明"
            rules={[{ required: true, message: "必須項目です" }]}
          >
            <TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Setting_LinkRecommend;
